import { useEffect, useState, useCallback, useRef } from 'react'
import { HttpClient } from '@opallabs/api'
import { getNestedProperty } from '@opallabs/lambda'
import {
  ContentPreviewState,
  ContentPreviewStateReturn,
  GetContentByIDV2Return,
} from '../../types'
import { validations } from '../../validations'

export type TwitterContentPreviewProps = {
  embeddedUrl: string
  embeddedUrlRegex: RegExp
  getContentByIdV2?: (id: number) => Promise<GetContentByIDV2Return>
  onSnapshotUrlChange: (val: string) => void
  snapshotUrl: string
}

export default function useTwitterContentPreview({
  embeddedUrl,
  embeddedUrlRegex,
  getContentByIdV2,
  onSnapshotUrlChange,
  snapshotUrl,
}: TwitterContentPreviewProps): ContentPreviewStateReturn {
  const [embedlyPreview, setEmbedlyPreview] = useState<null | string>(null)
  const [state, setState] = useState<ContentPreviewState>(
    embeddedUrl ? ContentPreviewState.LOADING : ContentPreviewState.EMPTY
  )

  const setSnapshotUrlChange = useCallback(
    (val) => {
      onSnapshotUrlChange(val)
    },
    [onSnapshotUrlChange]
  )

  const getContentByIdV2Ref = useRef(getContentByIdV2)
  const setSnapshotUrlChangeRef = useRef(setSnapshotUrlChange)

  useEffect(() => {
    setState(ContentPreviewState.EMPTY)
    if (embeddedUrl && getContentByIdV2Ref.current) {
      setState(ContentPreviewState.LOADING)
      // Fetch current default snapshot url for opal content
      if (validations.opalContentValidation.test(embeddedUrl)) {
        // Fetch opal content if snapshotUrl isn't already set or
        // if it's being refreshed
        if (!snapshotUrl || snapshotUrl === 'snapshot_url') {
          // Grab the post ID from the end of embeddedUrl
          const embeddedUrlMatch = (
            embeddedUrl.match(/posts\/\d+/g) as RegExpMatchArray
          )[0]
          const contentId: number = +embeddedUrlMatch?.slice(6) || 0
          getContentByIdV2Ref
            .current(contentId)
            .then((response: GetContentByIDV2Return) => {
              const contentType = getNestedProperty(
                response,
                'payload',
                'content_type'
              ) as string
              // Ensure that the content type is a Tweet
              if (contentType.includes('twitter')) {
                const newSnapshotUrl = getNestedProperty(
                  response,
                  'payload',
                  'default_snapshot_url'
                ) as string
                // Edge case: retweeting from moment shroud will fetch a link but not an image
                // so we need to display the tweet url input instead
                setSnapshotUrlChangeRef.current(newSnapshotUrl)
                setState(ContentPreviewState.LOADED)
                if (
                  !snapshotUrl &&
                  !window.location.pathname.includes('posts')
                ) {
                  setState(ContentPreviewState.EMPTY)
                  return newSnapshotUrl
                }
                return newSnapshotUrl
              }
              setState(ContentPreviewState.ERROR)
              return ''
            })
            .catch(() => {
              setState(ContentPreviewState.ERROR)
            })
        } else {
          setState(ContentPreviewState.LOADED)
        }
        // Fetch embed.ly view of existing tweet
      } else if (embeddedUrlRegex.test(embeddedUrl)) {
        HttpClient()
          .post(
            `/links/extract_preview?url=${encodeURIComponent(
              embeddedUrl
            )}&omit_script=true`
          )
          .then((response) => {
            const htmlResponse: string | undefined = getNestedProperty(
              response,
              'data',
              'media',
              'html'
            )
            // Ensure that the html is not empty
            if (htmlResponse) {
              const script = document.createElement('script')
              script.src = 'https://platform.twitter.com/widgets.js'
              script.classList.add('twitter-wjs')
              script.async = true
              document.body.appendChild(script)
              script.onload = () => {
                setState(ContentPreviewState.LOADED)
              }
              setEmbedlyPreview(htmlResponse)
              setSnapshotUrlChangeRef.current('')
              setState(ContentPreviewState.LOADED)
              return embedlyPreview
            }
            setState(ContentPreviewState.ERROR)
            return embedlyPreview
          })
          .catch(() => {
            setState(ContentPreviewState.ERROR)
          })
      } else {
        setState(ContentPreviewState.ERROR)
      }
    }
    return () => {
      const scripts = document.getElementsByClassName('twitter-wjs')
      if (scripts.length > 0) {
        document.body.removeChild(scripts[0])
      }
    }
  }, [embeddedUrl, embeddedUrlRegex, embedlyPreview, snapshotUrl])

  return {
    embedlyPreview,
    state,
  }
}
